import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import './Settings.css';

export default function Settings() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ username: '', full_name: '', location: '', bio: '' });
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [darkMode, setDarkMode] = useState(localStorage.getItem('theme') !== 'light');
  const [activeSection, setActiveSection] = useState('profile');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (profile) {
      setForm({
        username:  profile.username  || '',
        full_name: profile.full_name || '',
        location:  profile.location  || '',
        bio:       profile.bio       || '',
      });
    }
  }, [user, profile]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
    }
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const upd = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const updPw = e => setPasswordForm(p => ({ ...p, [e.target.name]: e.target.value }));

  async function saveProfile(e) {
    e.preventDefault();
    setSaving(true); setProfileMsg('');
    const { error } = await supabase.from('profiles').update({
      username:  form.username,
      full_name: form.full_name,
      location:  form.location,
      bio:       form.bio,
    }).eq('id', user.id);

    if (error) setProfileMsg('Error: ' + error.message);
    else setProfileMsg('Profile saved successfully.');
    setSaving(false);
  }

  async function savePassword(e) {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMsg('Passwords do not match.'); return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordMsg('Password must be at least 6 characters.'); return;
    }
    setSavingPw(true); setPasswordMsg('');
    const { error } = await supabase.auth.updateUser({ password: passwordForm.newPassword });
    if (error) setPasswordMsg('Error: ' + error.message);
    else { setPasswordMsg('Password updated successfully.'); setPasswordForm({ newPassword:'', confirmPassword:'' }); }
    setSavingPw(false);
  }

  async function handleDeleteAccount() {
    if (!window.confirm('Are you sure you want to delete your account? This cannot be undone.')) return;
    if (!window.confirm('This will permanently delete all your listings and messages. Are you absolutely sure?')) return;
    await signOut();
    navigate('/');
  }

  const SECTIONS = [
    { id: 'profile',    label: 'Profile'         },
    { id: 'account',    label: 'Account'         },
    { id: 'appearance', label: 'Appearance'      },
    { id: 'danger',     label: 'Danger Zone'     },
  ];

  return (
    <div className="settings-page page-enter">
      <div className="container">
        <h1 className="settings-title">Settings</h1>

        <div className="settings-layout">
          {/* Sidebar */}
          <aside className="settings-nav">
            {SECTIONS.map(s => (
              <button
                key={s.id}
                className={`settings-nav-item ${activeSection === s.id ? 'active' : ''} ${s.id === 'danger' ? 'danger' : ''}`}
                onClick={() => setActiveSection(s.id)}
              >
                {s.label}
              </button>
            ))}
          </aside>

          {/* Content */}
          <div className="settings-content">

            {/* Profile */}
            {activeSection === 'profile' && (
              <div className="settings-card">
                <h2 className="settings-card-title">Profile Information</h2>
                <p className="settings-card-sub">This is how other users see you on Electradez.</p>
                {profileMsg && <div className={`alert ${profileMsg.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>{profileMsg}</div>}
                <form onSubmit={saveProfile}>
                  <div className="form-row-2">
                    <div className="form-group">
                      <label className="form-label">Username</label>
                      <input name="username" className="form-input" value={form.username} onChange={upd} required minLength={3} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Full Name</label>
                      <input name="full_name" className="form-input" placeholder="Optional" value={form.full_name} onChange={upd} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Location</label>
                    <input name="location" className="form-input" placeholder="City, State" value={form.location} onChange={upd} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Bio</label>
                    <textarea name="bio" className="form-textarea" placeholder="Tell the community about yourself and your rides..." value={form.bio} onChange={upd} style={{minHeight:100}} />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Profile'}
                  </button>
                </form>
              </div>
            )}

            {/* Account */}
            {activeSection === 'account' && (
              <div className="settings-card">
                <h2 className="settings-card-title">Account</h2>
                <p className="settings-card-sub">Manage your login credentials.</p>

                <div className="settings-info-row">
                  <span className="settings-info-label">Email</span>
                  <span className="settings-info-value">{user?.email}</span>
                </div>

                <div className="settings-divider" />

                <h3 className="settings-sub-title">Change Password</h3>
                {passwordMsg && <div className={`alert ${passwordMsg.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>{passwordMsg}</div>}
                <form onSubmit={savePassword}>
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <input name="newPassword" type="password" className="form-input" placeholder="Min 6 characters" value={passwordForm.newPassword} onChange={updPw} required minLength={6} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm New Password</label>
                    <input name="confirmPassword" type="password" className="form-input" placeholder="Repeat password" value={passwordForm.confirmPassword} onChange={updPw} required />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={savingPw}>
                    {savingPw ? 'Updating...' : 'Update Password'}
                  </button>
                </form>
              </div>
            )}

            {/* Appearance */}
            {activeSection === 'appearance' && (
              <div className="settings-card">
                <h2 className="settings-card-title">Appearance</h2>
                <p className="settings-card-sub">Customize how Electradez looks for you.</p>

                <div className="settings-toggle-row">
                  <div>
                    <p className="toggle-label">Dark Mode</p>
                    <p className="toggle-sub">Use the dark theme (recommended)</p>
                  </div>
                  <button
                    className={`toggle-switch ${darkMode ? 'on' : 'off'}`}
                    onClick={() => setDarkMode(!darkMode)}
                    type="button"
                  >
                    <span className="toggle-knob" />
                  </button>
                </div>

                <div className="settings-divider" />

                <div className="theme-preview">
                  <p className="settings-info-label" style={{marginBottom:12}}>Current Theme</p>
                  <div className={`theme-swatch ${darkMode ? 'dark' : 'light'}`}>
                    <span>{darkMode ? 'Dark' : 'Light'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Danger Zone */}
            {activeSection === 'danger' && (
              <div className="settings-card danger-card">
                <h2 className="settings-card-title danger-title">Danger Zone</h2>
                <p className="settings-card-sub">These actions are permanent and cannot be undone.</p>

                <div className="danger-row">
                  <div>
                    <p className="danger-row-title">Sign Out Everywhere</p>
                    <p className="danger-row-sub">Sign out of all devices and sessions.</p>
                  </div>
                  <button className="btn btn-ghost" onClick={signOut}>Sign Out</button>
                </div>

                <div className="settings-divider" />

                <div className="danger-row">
                  <div>
                    <p className="danger-row-title">Delete Account</p>
                    <p className="danger-row-sub">Permanently delete your account and all your data.</p>
                  </div>
                  <button className="btn btn-danger" onClick={handleDeleteAccount}>Delete Account</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
